/**
 * placearm.ts — on a touch-first device, one tap never sends an order (FR3.3).
 *
 * Intent 261018-mobile-v1, unit U3 (2026-10-18). Since c0e36402 ("one-button bracket entry")
 * one press of `#tk-place` calls `tkPlace()`, which sends `POST /api/trade/bracket` (or
 * `/api/trade/modify` on a live position). On a desktop that press is deliberate. On a phone
 * the button is a 44 px full-width target in a bottom sheet over the chart, and a thumb that
 * scrolls the sheet can land on it. So on a COARSE pointer the first tap only ARMS the button
 * ("Tap again to place") and a second tap within ARM_WINDOW_MS places. On a fine pointer one
 * press still places, exactly as before.
 *
 * WHAT THIS IS NOT: a check. It sits in FRONT of the unchanged `tkPlace()` and can only add a
 * refusal (SEC-U3.2); the request, the server's checks and `risk_gates` are untouched (FR3.5).
 *
 * The arm is bound to the prices it was armed on (`ticketKey`): an edit between the two taps
 * re-arms instead of placing prices the user did not see when they armed. Pure, no DOM, no
 * clock: `now` is passed in so the window is testable.
 */
import type { TicketState } from './ticket';

/** How long an arm stays live. Long enough for a deliberate second tap, short enough that a
 *  stale arm does not wait under a thumb. */
export const ARM_WINDOW_MS = 4000;

export type PlaceArm = { at: number; key: string } | null;

/** The prices the arm is bound to: market (or the managed position), side, entry, stop, tp1. */
export function ticketKey(
  st: Pick<TicketState, 'managing' | 'symbol' | 'side' | 'entry' | 'stop' | 'tps'>,
): string {
  return [st.managing ?? st.symbol, st.side, st.entry, st.stop, st.tps[0] ?? null]
    .map((v) => String(v))
    .join('|');
}

/** Whether `arm` is live for this ticket at `now`. Drives the button label. */
export function isArmed(arm: PlaceArm, now: number, key: string): boolean {
  return arm !== null && arm.key === key && now - arm.at < ARM_WINDOW_MS;
}

/** One tap on `#tk-place`. `place` means call `tkPlace()`; `arm` means show the armed label. */
export function placeStep(
  arm: PlaceArm,
  input: { coarse: boolean; now: number; key: string },
): { action: 'place' | 'arm'; arm: PlaceArm } {
  if (!input.coarse || isArmed(arm, input.now, input.key)) return { action: 'place', arm: null };
  return { action: 'arm', arm: { at: input.now, key: input.key } };
}
